import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Button,
  LoadingSpinner,
  PageHeader,
  ShareSheet,
} from '../components';
import { eventService } from '../services';

/**
 * EventSharePage - Event Created/Published Success (Screen 06B)
 * Design: Celebratory with prominent share links
 */
export default function EventSharePage() {
  const navigate = useNavigate();
  const { id: eventId } = useParams();
  const location = useLocation();

  const [event, setEvent] = useState(location.state?.event || null);
  const [isLoading, setIsLoading] = useState(!event);
  const [error, setError] = useState(null);
  const [copyLinkSuccess, setCopyLinkSuccess] = useState(false);
  const [copyCodeSuccess, setCopyCodeSuccess] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Determine if published from location state
  const wasPublished = location.state?.published ?? event?.status === 'published';

  // Fetch event if not in state
  useEffect(() => {
    if (!event && eventId) {
      const fetchEvent = async () => {
        setIsLoading(true);
        try {
          const result = await eventService.getEvent(eventId);
          if (result.success) {
            setEvent(result.data);
          } else {
            throw new Error(result.error || 'Failed to load event');
          }
        } catch (err) {
          setError(err.message);
        } finally {
          setIsLoading(false);
        }
      };
      fetchEvent();
    }
  }, [event, eventId]);

  // Generate event URL
  const getEventUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/events/${event?.inviteCode || eventId}`;
  };

  // Get invite code
  const getInviteCode = () => {
    return event?.inviteCode || eventId;
  };

  // Copy link to clipboard
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getEventUrl());
      setCopyLinkSuccess(true);
      setTimeout(() => setCopyLinkSuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  // Copy code to clipboard
  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(getInviteCode());
      setCopyCodeSuccess(true);
      setTimeout(() => setCopyCodeSuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <LoadingSpinner size="lg" label="Loading event..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-mono text-xl font-bold uppercase tracking-wider text-black mb-4">
            ERROR
          </h1>
          <p className="font-mono text-sm text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/host')}>GO TO DASHBOARD</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50">
      <PageHeader
        title={wasPublished ? 'Event Published!' : 'Event Created!'}
        breadcrumbs={[
          { label: 'Dashboard', onClick: () => navigate('/host') },
          { label: event?.title || 'Event' },
        ]}
      />

      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Success message */}
        <div className="text-center mb-12">
          {/* Success icon */}
          <div className="w-20 h-20 mx-auto mb-6 border-4 border-black flex items-center justify-center bg-white">
            <svg
              className="w-10 h-10 text-black"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="square"
                strokeLinejoin="miter"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <h2 className="font-mono text-2xl font-bold uppercase tracking-wider text-black mb-4">
            {wasPublished ? 'YOUR EVENT IS LIVE!' : 'DRAFT SAVED!'}
          </h2>

          <p className="font-mono text-sm text-gray-600 max-w-md mx-auto">
            {wasPublished
              ? 'Share your event with potential guests. They can apply using the link or code below.'
              : 'Your event has been saved as a draft. You can publish it when ready from the operations page.'}
          </p>
        </div>

        {/* Event info card */}
        <div className="border-2 border-black p-6 mb-8">
          <h3 className="font-mono text-lg font-bold uppercase tracking-wider text-black mb-2">
            {event?.title}
          </h3>
          <p className="font-mono text-sm text-gray-600 mb-4">
            {event?.date
              ? new Date(event.date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : 'Date TBD'}
          </p>
          <div className="flex items-center gap-2">
            <span
              className={`
                px-3 py-1 font-mono text-xs font-semibold uppercase tracking-wider border
                ${
                  event?.status === 'published'
                    ? 'bg-green-100 text-green-800 border-green-500'
                    : 'bg-yellow-100 text-yellow-800 border-yellow-500'
                }
              `}
            >
              {event?.status || 'DRAFT'}
            </span>
            <span className="px-3 py-1 font-mono text-xs font-semibold uppercase tracking-wider border border-black bg-gray-100">
              {event?.mode === 'curated' ? 'CURATED' : 'OPEN'}
            </span>
          </div>
        </div>

        {/* Share section */}
        <div className="space-y-6">
          {/* Event URL */}
          <div>
            <label className="block font-mono text-sm font-semibold uppercase tracking-wider mb-2 text-black">
              Event Link
            </label>
            <div className="flex gap-2">
              <div className="flex-1 px-4 py-3 border-2 border-black bg-gray-50 overflow-hidden">
                <p className="font-mono text-sm text-black truncate">{getEventUrl()}</p>
              </div>
              <Button
                variant={copyLinkSuccess ? 'primary' : 'secondary'}
                onClick={handleCopyLink}
              >
                {copyLinkSuccess ? 'COPIED!' : 'COPY LINK'}
              </Button>
            </div>
          </div>

          {/* Invite Code */}
          <div>
            <label className="block font-mono text-sm font-semibold uppercase tracking-wider mb-2 text-black">
              Invite Code
            </label>
            <div className="flex gap-2">
              <div className="flex-1 px-4 py-3 border-2 border-black bg-gray-50">
                <p className="font-mono text-2xl font-bold tracking-widest text-black text-center">
                  {getInviteCode()}
                </p>
              </div>
              <Button
                variant={copyCodeSuccess ? 'primary' : 'secondary'}
                onClick={handleCopyCode}
              >
                {copyCodeSuccess ? 'COPIED!' : 'COPY CODE'}
              </Button>
            </div>
            <p className="mt-2 font-mono text-xs text-gray-500">
              Guests can use this code to find and apply to your event
            </p>
          </div>

          {/* Share to platforms */}
          <div className="pt-2">
            <Button
              variant="primary"
              fullWidth
              onClick={() => setShareOpen(true)}
            >
              SHARE TO...
            </Button>
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-12 flex flex-col sm:flex-row gap-4">
          <Button
            variant="primary"
            onClick={() => navigate(`/host/events/${eventId}`)}
            fullWidth
          >
            GO TO EVENT OPERATIONS
          </Button>
          <Button
            variant="secondary"
            onClick={() => navigate('/host/events/create')}
            fullWidth
          >
            CREATE ANOTHER EVENT
          </Button>
        </div>

        {/* Dashboard link */}
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => navigate('/host')}
            className="font-mono text-sm text-gray-600 uppercase tracking-wider hover:text-black transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>

      {/* Share Sheet Modal */}
      <ShareSheet
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        title={event?.title || ''}
        url={getEventUrl()}
        inviteCode={getInviteCode()}
        date={event?.date || event?.dateTime}
        status={event?.status}
        mode={event?.mode}
      />
    </div>
  );
}
